import React, { useState, useEffect } from 'react';
import {
  compareTableValues,
  getNextSortConfig,
  getSortIndicator,
  getUniqueOptions,
  matchesColumnFilter,
  matchesDateTimeRangeFilter,
  matchesSelectFilter,
  matchesTriStateFilter,
} from './tableUtils';
import { ClearableInput, ClearableRangeInputs, FilterToolbar } from './FilterControls';
import { MILITARY_SHOP_OPTIONS } from './militaryOptions';

// Picklist options for shop names
const usafShopOptions = MILITARY_SHOP_OPTIONS;

export default function IssuesLog({ 
    selectedAtoRow, 
    user, 
    selectedBase,
    api, 
    issues, 
    refreshIssues, 
    personnelData, 
    setAlerts 
}) {
  const operationLocation = selectedAtoRow?.location || selectedBase || user.location;
  // Form state
  const [tail, setTail] = useState("");
  const [spot, setSpot] = useState("");
  const [shopName, setShopName] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [sortConfig, setSortConfig] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [description, setDescription] = useState("");
  const [called, setCalled] = useState("");
  const [arrived, setArrived] = useState("");
  const [fixed, setFixed] = useState("");
  const [redBallFlag, setRedBallFlag] = useState(false);
  const [saveError, setSaveError] = useState('');


  // DISPLAY (table/labels): "YYYY-MM-DD HH:MM" in local time
  const fmtLocal = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return '';
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };


  // For binding to <input type="datetime-local"> (no timezone in value)
  const fmtLocalInput = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (isNaN(d)) return '';
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // For saving to server in UTC (ISO with Z)
  const toUtcIso = (localStr) => localStr ? new Date(localStr).toISOString() : null;


  // Selection & editing state
  const [selectedIssueId, setSelectedIssueId] = useState(null);
  const [showRedBall, setShowRedBall] = useState(false);
  const [showRedBallPersonnelDetails, setShowRedBallPersonnelDetails] = useState(false);
  const [redBallData, setRedBallData] = useState({
    rb_jcn: '',
    rb_wuc: '',
    rb_discrepancy: '',
    rb_etic: '',
    rb_estimated_completion: '',
    rb_corrective_action: '',
    rb_discovered_by_emp_num: '', rb_discovered_by_name: '',
    rb_corrected_by_emp_num: '', rb_corrected_by_name: '',
    rb_inspected_by_emp_num: '', rb_inspected_by_name: ''
  });

  // AGE Requests state
  const [ageRequests, setAgeRequests] = useState([]);

  // Sync form with selected ATO row
  useEffect(() => {
    if (selectedAtoRow) {
      setTail(selectedAtoRow.tail || '');
      setSpot(selectedAtoRow.spot || '');
    }
  }, [selectedAtoRow]);

  // Build AGE requests list
  useEffect(() => {
    const norm = p => (p && p > 0 ? p : Number.POSITIVE_INFINITY);
    const tie = (a, b) => {
      // secondary key to prevent “snap back” when priorities tie
      // prefer earliest createdAt (or id as a final tie-breaker)
      const aT = a.createdAt || "";
      const bT = b.createdAt || "";
      if (aT < bT) return -1;
      if (aT > bT) return 1;
      return (a.id || "").localeCompare(b.id || "");
    };
  
    const list = issues
      .filter(i => i.shopName === "AGE" && !i.fixed)
      .slice()
      .sort((a, b) => {
        const d = norm(a.priority) - norm(b.priority);
        return d !== 0 ? d : tie(a, b);
      })
      .map((issue, index) => ({ ...issue, priority: index + 1 }));
  
    setAgeRequests(list);
  }, [issues]);

  // Lookup name helper
  const lookupName = num => {
    const rec = personnelData.find(p => String(p['Emp Num']) === String(num));
    return rec ? `${rec['First Name']} ${rec['Last Name']}` : '';
  };

  // Populate form for editing
  const handleRowSelect = issue => {
    if (selectedIssueId === issue.id) {
      resetForm();
    } else {
      setSelectedIssueId(issue.id);
      setTail(issue.tail || '');
      setSpot(issue.spot || '');
      setShopName(issue.shopName || '');
      setDescription(issue.description || '');
      setCalled(issue.called || '');
      setArrived(issue.arrived || '');
      setFixed(issue.fixed || '');
      setRedBallFlag(issue.redBallFlag || false);
      setShowRedBall(issue.redBallFlag || false);
      const nextRedBallData = {
        rb_jcn: issue.redBall?.rb_jcn || '',
        rb_wuc: issue.redBall?.rb_wuc || '',
        rb_discrepancy: issue.redBall?.rb_discrepancy || '',
        rb_etic: issue.redBall?.rb_etic || '',
        rb_estimated_completion: issue.redBall?.rb_estimated_completion || '',
        rb_corrective_action: issue.redBall?.rb_corrective_action || '',
        rb_discovered_by_emp_num: issue.redBall?.rb_discovered_by_emp_num || '',
        rb_discovered_by_name: issue.redBall?.rb_discovered_by_name || '',
        rb_corrected_by_emp_num: issue.redBall?.rb_corrected_by_emp_num || '',
        rb_corrected_by_name: issue.redBall?.rb_corrected_by_name || '',
        rb_inspected_by_emp_num: issue.redBall?.rb_inspected_by_emp_num || '',
        rb_inspected_by_name: issue.redBall?.rb_inspected_by_name || ''
      };
      setRedBallData(nextRedBallData);
      setShowRedBallPersonnelDetails(false);
    }
  };

  // Handle red-ball input
  const handleRedBallChange = e => {
    const { name, value } = e.target;
    const normalizedValue = name === 'rb_wuc'
      ? value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
      : value;
    const updated = { ...redBallData, [name]: normalizedValue };
  
    if (name === 'rb_etic') {
      const eticMinutes = parseInt(value, 10);
      if (!isNaN(eticMinutes)) {
        const now = new Date();
        now.setMinutes(now.getMinutes() + eticMinutes);
  
        const pad = n => String(n).padStart(2, '0');
        updated.rb_estimated_completion =
          `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      } else {
        updated.rb_estimated_completion = '';
      }
    }
  
    // Auto-fill names based on EmpNum lookup
    if (name.endsWith('_emp_num')) {
      let nameKey = name.replace('_emp_num', '_name');  
      updated[nameKey] = lookupName(value);
    }
  
    setRedBallData(updated);
  };

  // Reset form to new-issue state
  const resetForm = () => {
    setSaveError('');
    setSelectedIssueId(null);
    setTail(''); setSpot(''); setShopName(''); setDescription('');
    setCalled(''); setArrived(''); setFixed(''); setRedBallFlag(false);
    setShowRedBall(false);
    setShowRedBallPersonnelDetails(false);
      // redBallData now uses rb_* keys to match backend & DB
    setRedBallData({
      rb_jcn: '',
      rb_wuc: '',
      rb_discrepancy: '',
      rb_etic: '',
      rb_estimated_completion: '',
      rb_corrective_action: '',
      rb_discovered_by_emp_num: '',
      rb_discovered_by_name: '',
      rb_corrected_by_emp_num: '',
      rb_corrected_by_name: '',
      rb_inspected_by_emp_num: '',
      rb_inspected_by_name: ''
    });
  };

  // Unified Save for create or update
  const handleSave = () => {
    setSaveError('');
    const now = new Date().toLocaleString();
    if (selectedIssueId) {
      const id       = selectedIssueId;
      const location = operationLocation;
      // Gather everything into one updates object
      const updates = {
        tail,
        spot,
        shopName,
        description,
        called: toUtcIso(called),
        arrived: arrived ? toUtcIso(arrived) : null,
        fixed: fixed ? toUtcIso(fixed) : null,
        redBallFlag,   // always send the checkbox state
  
        rb_discrepancy: redBallData.rb_discrepancy,
        rb_jcn: redBallData.rb_jcn,
        rb_wuc: redBallData.rb_wuc,
        rb_etic: redBallData.rb_etic,
        rb_estimated_completion: redBallData.rb_estimated_completion
          ? toUtcIso(redBallData.rb_estimated_completion)
          : null,
        rb_corrective_action: redBallData.rb_corrective_action,
        rb_discovered_by_emp_num: redBallData.rb_discovered_by_emp_num,
        rb_discovered_by_name: redBallData.rb_discovered_by_name,
        rb_corrected_by_emp_num: redBallData.rb_corrected_by_emp_num,
        rb_corrected_by_name: redBallData.rb_corrected_by_name,
        rb_inspected_by_emp_num: redBallData.rb_inspected_by_emp_num,
        rb_inspected_by_name: redBallData.rb_inspected_by_name,
  
        updated_at: now
      };
  
      api.post('/update-issues-log-entry', { id, updates, location, user: user.email })
        .then(res => {
          resetForm();
          refreshIssues();
          (res.data.alerts || []).forEach(a => {
            setAlerts(prev => [
              {
                id: a.id || Date.now(),
                message: `[${a.tail}] Issue updated: ${a.details}`
              },
              ...prev
            ]);
          });
        })
        .catch(err => {
          console.error(err);
          setSaveError(err.response?.data?.message || 'Unable to update this issue.');
        });
    } else {
      // Create new issue
      const payload = {
        tail,
        spot,
        shopName,
        description,
        called: toUtcIso(called),
        arrived: arrived ? toUtcIso(arrived) : null,
        fixed: fixed ? toUtcIso(fixed) : null,
        redBallFlag,
  
        rb_discrepancy: redBallData.rb_discrepancy,
        rb_jcn: redBallData.rb_jcn,
        rb_wuc: redBallData.rb_wuc,
        rb_etic: redBallData.rb_etic,
        rb_estimated_completion: redBallData.rb_estimated_completion
          ? toUtcIso(redBallData.rb_estimated_completion)
          : null,
        rb_corrective_action: redBallData.rb_corrective_action,
        rb_discovered_by_emp_num: redBallData.rb_discovered_by_emp_num,
        rb_discovered_by_name: redBallData.rb_discovered_by_name,
        rb_corrected_by_emp_num: redBallData.rb_corrected_by_emp_num,
        rb_corrected_by_name: redBallData.rb_corrected_by_name,
        rb_inspected_by_emp_num: redBallData.rb_inspected_by_emp_num,
        rb_inspected_by_name: redBallData.rb_inspected_by_name,
  
        user: user.email,
        location: operationLocation,
        atoId: selectedAtoRow?.id || null
      };
  
      api.post('/add-issues-log', payload)
        .then(res => {
          resetForm();
          refreshIssues();
          (res.data.alerts || []).forEach(a => {
            setAlerts(prev => [
              {
                message: `[${a.tail}] New issue: ${payload.description}`
              },
              ...prev
            ]);
          });
        })
        .catch(err => {
          console.error(err);
          setSaveError(err.response?.data?.message || 'Unable to save this issue.');
        });
    }
  };


  const saveField = (issueId, field, value) => {
    api.post('/update-issues-log-entry', {
      id:       issueId,
      updates:  { [field]: value },
      location: operationLocation,
      user:     user.email
    })
    .then(res => {
      // push any returned alerts into your bell-icon notifications
      (res.data.alerts || []).forEach(a => {
        setAlerts(prev => [
          { message: `[${a.tail}] ${field}: ${value}` },
          ...prev
        ]);
      });
      // re-load the full issues list (so your AGE list re-indexes)
      refreshIssues();
    })
    .catch(console.error);
  };

  // Debounce to prevent racey double clicks
  const [savingPriority, setSavingPriority] = React.useState(false);
  
  // Adjust AGE priority (renumber all + persist all)
  const movePriority = async (id, direction) => {
    if (savingPriority) return;
  
    const idx = ageRequests.findIndex(i => i.id === id);
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swap < 0 || swap >= ageRequests.length) return;
  
    // 1) Reorder locally
    const reordered = [...ageRequests];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
  
    // 2) Renumber EVERY row 1..N (so no one keeps 0)
    const renumbered = reordered.map((item, i) => ({ ...item, priority: i + 1 }));
  
    // 3) Optimistic UI
    setAgeRequests(renumbered);
  
    // 4) Persist all rows’ priorities to keep DB consistent
    try {
      setSavingPriority(true);
      for (const item of renumbered) {
        await api.post("/update-issues-log-entry", {
          id: item.id,
          updates: { priority: item.priority },
          user: user.email,
          location: operationLocation,
          timezone: "America/New_York"
        });
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
    } finally {
      setSavingPriority(false);
      // 5) Reload authoritative list (prevents snap-back)
      await refreshIssues();
    }
  };

  const issueTableColumns = [
    { key: 'tail', label: 'Tail #' },
    { key: 'spot', label: 'Spot' },
    { key: 'shopName', label: 'Shop Name' },
    { key: 'description', label: 'Issue' },
    { key: 'called', label: 'Called' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'fixed', label: 'Fixed' },
    { key: 'redBallFlag', label: 'Red Ball?' },
    { key: 'status', label: 'Status' },
  ];
  const issueDateTimeRangeKeys = ['called', 'arrived', 'fixed'];
  const issueShopOptions = getUniqueOptions(
    [{ shopName: '' }, ...issues, ...usafShopOptions.map(option => ({ shopName: option }))],
    issue => issue.shopName
  );

  const getIssueCellValue = (issue, key) => {
    if (key === 'called' || key === 'arrived' || key === 'fixed') {
      return fmtLocal(issue[key]);
    }

    if (key === 'redBallFlag') {
      return issue.redBallFlag ? 'True' : 'False';
    }

    if (key === 'status') {
      return issue.fixed ? 'Closed' : 'Open';
    }

    return issue[key];
  };

  const handleSort = key => {
    setSortConfig(current => getNextSortConfig(current, key));
  };

  const handleFilterChange = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilter = key => {
    setColumnFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const handleDateTimeRangeFilterChange = (key, bound, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: {
        from: prev[key]?.from || '',
        to: prev[key]?.to || '',
        [bound]: value,
      },
    }));
  };

  const matchesIssueFilter = (issue, key) => {
    if (key === 'shopName') {
      return matchesSelectFilter(issue.shopName, columnFilters.shopName);
    }

    if (issueDateTimeRangeKeys.includes(key)) {
      return matchesDateTimeRangeFilter(issue[key], columnFilters[key] || {});
    }

    if (key === 'redBallFlag') {
      return matchesTriStateFilter(issue.redBallFlag ? 'true' : 'false', columnFilters.redBallFlag);
    }

    if (key === 'status') {
      return matchesSelectFilter(issue.fixed ? 'Closed' : 'Open', columnFilters.status);
    }

    return matchesColumnFilter(getIssueCellValue(issue, key), columnFilters[key]);
  };

  const renderIssueFilterCell = key => {
    if (key === 'shopName') {
      return (
        <select
          value={columnFilters.shopName || ''}
          onChange={e => handleFilterChange('shopName', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="">All</option>
          {issueShopOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    if (issueDateTimeRangeKeys.includes(key)) {
      const range = columnFilters[key] || { from: '', to: '' };

      return (
        <ClearableRangeInputs
          type="datetime-local"
          from={range.from}
          to={range.to}
          onFromChange={e => handleDateTimeRangeFilterChange(key, 'from', e.target.value)}
          onToChange={e => handleDateTimeRangeFilterChange(key, 'to', e.target.value)}
        />
      );
    }

    if (key === 'redBallFlag') {
      return (
        <select
          value={columnFilters.redBallFlag || 'any'}
          onChange={e => handleFilterChange('redBallFlag', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="any">Any</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    if (key === 'status') {
      return (
        <select
          value={columnFilters.status || ''}
          onChange={e => handleFilterChange('status', e.target.value)}
          style={{ width: '100%' }}
        >
          <option value="">All</option>
          <option value="Open">Open</option>
          <option value="Closed">Closed</option>
        </select>
      );
    }

    return (
      <ClearableInput
        type="text"
        value={columnFilters[key] || ''}
        onChange={e => handleFilterChange(key, e.target.value)}
        placeholder={`Filter ${issueTableColumns.find(col => col.key === key)?.label || key}`}
      />
    );
  };

  // Replace all usage of `issues` in the UI with `filteredIssues`
  const filteredIssues = issues.filter(issue => {
    if (statusFilter === "open")   return !issue.fixed;
    if (statusFilter === "closed") return Boolean(issue.fixed);
    return true; // "all"
  });

  const displayedIssues = filteredIssues
    .filter(issue =>
      issueTableColumns.every(col =>
        matchesIssueFilter(issue, col.key)
      )
    )
    .slice()
    .sort((left, right) => {
      if (!sortConfig) return 0;

      return compareTableValues(
        getIssueCellValue(left, sortConfig.key),
        getIssueCellValue(right, sortConfig.key),
        sortConfig.direction
      );
    });

  const activeFilterChips = Object.entries(columnFilters).flatMap(([key, value]) => {
    if (!value || value === 'any') return [];

    if (typeof value === 'object') {
      const from = value.from || '';
      const to = value.to || '';
      if (!from && !to) return [];

      return [{
        key,
        label: `${issueTableColumns.find(col => col.key === key)?.label || key}: ${
          from && to ? `${from} to ${to}` : from ? `from ${from}` : `to ${to}`
        }`,
      }];
    }

    return [{
      key,
      label: `${issueTableColumns.find(col => col.key === key)?.label || key}: ${value}`,
    }];
  });

  const panelStyle = {
    flex: '1 1 300px',
    border: '1px solid #ccc',
    padding: '8px 16px 16px 16px',
    borderRadius: '4px'
  };

  const fieldStyle = { width: '100%' };

  const compactGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px 12px',
    alignItems: 'end'
  };

  const timeGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '10px 12px',
    alignItems: 'end',
    marginTop: '10px'
  };

  const redBallTwoColStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '10px 12px',
    alignItems: 'end'
  };

  const redBallThreeColStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 1.4fr)',
    gap: '10px 12px',
    alignItems: 'end'
  };

  return (
    <div className="issues-log-container" style={{ userSelect: 'none' }}>
      {/* Top: responsive 3-column */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '24px' }}>
        {/* Left: Issue form */}
        <div style={panelStyle}>
          <h3>{selectedIssueId ? 'Edit Issue' : 'Log New Issue'}</h3>
          <div style={compactGridStyle}>
            <div>
              <label>Tail #:</label>
              <input type="text" value={tail} onChange={e => setTail(e.target.value)} style={fieldStyle} />
            </div>
            <div>
              <label>Spot:</label>
              <input type="text" value={spot} onChange={e => setSpot(e.target.value)} style={fieldStyle} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Shop Name:</label>
              <select value={shopName} onChange={e => setShopName(e.target.value)} style={fieldStyle}>
                <option value="">-- Select Shop --</option>
                {usafShopOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Issue Reported:</label>
              <input type="text" value={description} onChange={e => setDescription(e.target.value)} style={fieldStyle} />
            </div>
          </div>
          <div style={timeGridStyle}>
            <div>
              <label>Called:</label>
              <input type="datetime-local" value={fmtLocalInput(called)} onChange={e => setCalled(e.target.value)} style={fieldStyle} />
            </div>
            <div>
              <label>Arrived:</label>
              <input type="datetime-local" value={fmtLocalInput(arrived)} onChange={e => setArrived(e.target.value)} style={fieldStyle} />
            </div>
            <div>
              <label>Fixed:</label>
              <input type="datetime-local" value={fmtLocalInput(fixed)} onChange={e => setFixed(e.target.value)} style={fieldStyle} />
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <label>
              <input
                type="checkbox" 
                checked={redBallFlag} 
                onChange={e => {
                  const checked = e.target.checked;
                  setRedBallFlag(checked);
                  setShowRedBall(checked);
                  if (!checked) {
                    setShowRedBallPersonnelDetails(false);
                  }
                }}
              /> Red Ball?
            </label>      
          </div>
          <div style={{ marginTop: '16px' }}>
            <button onClick={handleSave}>{selectedIssueId ? 'Update Issue' : 'Save Issue'}</button>
            {selectedIssueId && <button onClick={resetForm} style={{ marginLeft: 8 }}>Cancel</button>}
            {saveError ? <div role="alert" className="issues-form-error">{saveError}</div> : null}
          </div>
        </div>
        {/* Middle: Red Ball details */}
        <div
          style={{
            ...panelStyle,
            background: '#f9f9f9'
          }}
        >
          <h3>Red Ball Details</h3>
          {showRedBall ? (
            <>
              <div className="red-ball-discrepancy-grid">
                <div>
                  <label>Discrepancy:</label>
                  <textarea
                    rows={2}
                    name="rb_discrepancy"
                    value={redBallData.rb_discrepancy}
                    onChange={handleRedBallChange}
                    style={fieldStyle}
                  />
                </div>
                <div>
                  <label>WUC:</label>
                  <input
                    type="text"
                    name="rb_wuc"
                    value={redBallData.rb_wuc}
                    onChange={handleRedBallChange}
                    maxLength={8}
                    pattern="[A-Za-z0-9]{0,8}"
                    style={fieldStyle}
                    placeholder="e.g. 27A"
                    title="Work Unit Code, up to 8 letters and numbers"
                  />
                  <small>Work Unit Code</small>
                </div>
              </div>

              <div style={{ ...redBallThreeColStyle, marginBottom: '12px' }}>
                <div>
                  <label>JCN:</label>
                  <input
                    type="text"
                    name="rb_jcn"
                    value={redBallData.rb_jcn}
                    onChange={handleRedBallChange}
                    style={fieldStyle}
                    placeholder="Job Control Number"
                  />
                </div>
                <div>
                  <label>ETIC (minutes):</label>
                  <input
                    type="number"
                    name="rb_etic"
                    value={redBallData.rb_etic}
                    onChange={handleRedBallChange}
                    style={fieldStyle}
                  />
                </div>
                <div>
                  <label>Estimated Completion (Local):</label>
                  <input
                    type="datetime-local"
                    name="rb_estimated_completion"
                    value={redBallData.rb_estimated_completion}
                    readOnly
                    style={{ ...fieldStyle, background: '#eee', color: '#555' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label>Corrective Action:</label>
                <textarea
                  rows={2}
                  name="rb_corrective_action"
                  value={redBallData.rb_corrective_action}
                  onChange={handleRedBallChange}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowRedBallPersonnelDetails(prev => !prev)}
                  style={{
                    background: 'none',
                    border: '1px solid #c7d6e4',
                    borderRadius: '8px',
                    padding: '6px 10px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {showRedBallPersonnelDetails ? 'Hide Personnel Details' : 'Show Personnel Details'}
                </button>
              </div>

              {showRedBallPersonnelDetails && [
                { key: 'rb_discovered_by', label: 'Discovered' },
                { key: 'rb_corrected_by', label: 'Corrected' },
                { key: 'rb_inspected_by', label: 'Inspected' }
              ].map(({ key, label }) => (
                <div
                  key={key}
                  style={{ ...redBallTwoColStyle, marginBottom: '12px' }}
                >
                  <div>
                    <label>{label} By #:</label>
                    <input
                      type="number"
                      name={`${key}_emp_num`}
                      value={redBallData[`${key}_emp_num`]}
                      onChange={handleRedBallChange}
                      style={fieldStyle}
                    />
                  </div>
                  <div>
                    <label>Name:</label>
                    <input
                      type="text"
                      readOnly
                      value={redBallData[`${key}_name`]}
                      style={{ ...fieldStyle, background: '#eee' }}
                    />
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p>
              {selectedIssueId
                ? 'No Red Ball details. Click the checkbox on left to upgrade to redball.'
                : 'Select or create an issue to add Red Ball details.'}
            </p>
          )}
        </div>
        
        {/* Right: AGE Requests */}
        <div style={{ flex: '1 1 300px', border: '1px solid #ccc', padding: '8px 16px 16px 16px', borderRadius: '4px' }}>
          <h3>AGE Requests</h3>
          {ageRequests.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
              <thead>
                <tr><th>Priority</th><th>Tail #</th><th>Spot</th><th>Issue</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {ageRequests.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td><td>{r.tail}</td><td>{r.spot}</td><td style={{ textAlign: 'center' }}>{r.description}</td>
                    <td>    
                        <button onClick={() => movePriority(r.id,'up')}
                            disabled={idx === 0}>▲
                        </button>
                        <button onClick={() => movePriority(r.id,'down')}
                            disabled={idx === ageRequests.length - 1}>▼
                        </button>
                    </td>    
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p>No open AGE issues.</p>}
        </div>
      </div>
      <div className="issues-filter-bar">
        <label className="issues-filter-bar__status">
          <span>Show:</span>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="open">Open only</option>
            <option value="closed">Closed only</option>
            <option value="all">All</option>
          </select>
        </label>
        <FilterToolbar
          chips={activeFilterChips}
          onRemoveChip={clearFilter}
          onClearAll={clearAllFilters}
        />
      </div>
      {/* Bottom: Issues log table */}
      <div onClick={() => resetForm()} style={{ cursor: 'default' }}>
        <div className="table-shell">
        <table
          onClick={e => e.stopPropagation()}
          className="styled-table compact-table issues-table"
          style={{ width: '100%', borderCollapse: 'collapse'}}
        >
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}></th>
              {issueTableColumns.map(col => (
                <th key={col.key} style={{ textAlign: 'left' }}>
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', padding: 0 }}
                  >
                    {col.label}{getSortIndicator(sortConfig, col.key)}
                  </button>
                </th>
              ))}
              <th style={{ textAlign: 'left' }}>Actions</th></tr>
            </thead>
          <tbody>
            <tr>
              <td></td>
              {issueTableColumns.map(col => (
                <td key={`${col.key}-filter`}>
                  {renderIssueFilterCell(col.key)}
                </td>
              ))}
              <td></td>
            </tr>
          </tbody>
          <tbody>
            {displayedIssues.map(issue => (
              <tr key={issue.id} onClick={() => handleRowSelect(issue)} style={{ background: issue.id===selectedIssueId?'#eef':'transparent', cursor: 'pointer' }}>
                <td style={{ textAlign: 'left' }}><input type="radio" name="selectedIssue" checked={issue.id===selectedIssueId} readOnly /></td>
                <td style={{ textAlign: 'left' }}>{issue.tail}</td>
                <td style={{ textAlign: 'left' }}>{issue.spot}</td>
                <td style={{ textAlign: 'left' }}>{issue.shopName}</td>
                <td style={{ textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }}>{issue.description}</td>
                <td style={{ textAlign: 'left' }}>{fmtLocal(issue.called)}</td>
                <td style={{ textAlign: 'left' }}>{fmtLocal(issue.arrived)}</td>
                <td style={{ textAlign: 'left' }}>{fmtLocal(issue.fixed)}</td>
                <td className={issue.redBallFlag ? 'issues-table__redball-cell' : ''} style={{ textAlign: 'left' }}>
                  {issue.redBallFlag ? 'True' : 'False'}
                </td>
                <td style={{ textAlign: 'left' }}>{issue.fixed ? "Closed" : "Open"}</td>
                <td><button onClick={() => {
                  if(!window.confirm('Delete this issue?')) return;
                  api.post('/delete-issues-log-entry',{ location: operationLocation, id: issue.id, user: user.email })
                    .then(() => { resetForm(); refreshIssues(); })
                    .catch(err => {
                      console.error(err);
                      setSaveError(err.response?.data?.message || 'Unable to delete this issue.');
                    });
                }}>🗑️</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
